import { lazy, type ComponentType } from 'react';

type Module<P> = { default: ComponentType<P> };

/** The loader behind `lazyOptional`: one more try after a failure, then a part that renders nothing. */
export function optionalLoader<P extends object>(load: () => Promise<Module<P>>, retryMs = 1500): () => Promise<Module<P>> {
  return () =>
    load().catch(
      () =>
        new Promise<Module<P>>((resolve) =>
          setTimeout(() => load().then(resolve, () => resolve({ default: () => null })), retryMs),
        ),
    );
}

/**
 * A lazily loaded part a page can do without (decoration, a teaser). If its
 * file fails to load (a deploy still spreading across Cloudflare's edge, a flaky
 * phone network) it is tried once more, then left out: the page shows without
 * it instead of the whole screen turning into the error box, which is what a
 * plain `lazy` does.
 */
export function lazyOptional<P extends object>(load: () => Promise<Module<P>>, retryMs?: number) {
  return lazy(optionalLoader(load, retryMs));
}
